import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { useEffect } from "react";
import { scheduleRefresh, shouldSkipRefresh } from "./utils/auth";
import { ensureCsrf } from "./services/csrf";
import { HelmetProvider } from "react-helmet-async";

import Register from "./pages/Register";
import VerifyAccount from "./pages/VerifyAccount";
import Login from "./pages/Login";
import { EditorialHeader, EditorialFooter } from "./components/editorial/EditorialChrome";
import PremiumHome from "./pages/PremiumHome";
import ExperienceDiscovery from "./pages/ExperienceDiscovery";
import "./styles/editorial.css";
import CreateEvent from "./pages/CreateEvent";
import ProtectedRoute from "./context/ProtectedRoute";
import Events from "./pages/Events";
import JoshBakerYouMeArticle from "./pages/JoshBakerYouMeArticle";
import EastenderzHiIbizaArticle from "./pages/EastenderzHiIbizaArticle";
import EastenderzHiIbizaJuneArticle from "./pages/EastenderzHiIbizaJuneArticle";
import EastenderzHiIbizaSixteenJuneArticle from "./pages/EastenderzHiIbizaSixteenJuneArticle";
import EastenderzHiIbizaSevenJulyArticle from "./pages/EastenderzHiIbizaSevenJulyArticle";
import EastenderzHiIbizaSeptemberArticle from "./pages/EastenderzHiIbizaSeptemberArticle";
import EastenderzHiIbizaFifteenSeptemberArticle from "./pages/EastenderzHiIbizaFifteenSeptemberArticle";
import CarlCoxUnvrsArticle from "./pages/CarlCoxUnvrsArticle";
import JamieJonesParadiseUnvrsArticle from "./pages/JamieJonesParadiseUnvrsArticle";
import JamieJonesParadiseUnvrsJulyArticle from "./pages/JamieJonesParadiseUnvrsJulyArticle";
import JoyIbiza528Article from "./pages/JoyIbiza528Article";
import ChrisStussyPachaArticle from "./pages/ChrisStussyPachaArticle";
import DoNotSleepAmnesiaArticle from "./pages/DoNotSleepAmnesiaArticle";
import DoNotSleepAmnesiaJuneArticle from "./pages/DoNotSleepAmnesiaJuneArticle";
import Kaluki528IbizaArticle from "./pages/Kaluki528IbizaArticle";
import PurchaseTickets from "./pages/PurchaseTickets";
import PaymentSuccess from "./pages/PaymentSuccess";
import Account from "./pages/Account";
import AffiliateStats from "./pages/AffiliateStats";
import QRScannerPage from "./pages/QRScannerPage";
import AllAffiliateStats from "./pages/AllAffiliateStats";
import AdminEvents from "./pages/AdminEvents";
import Subscription from "./pages/Subscription";
import AdminVerificationDashboard from "./pages/AdminVerificationDashboard";
import MasterUser from "./pages/MasterUser";
import AdminBenefits from "./pages/AdminBenefits";
import SongSubmission from "./pages/SongSubmission";
import ResetPassword from "./pages/ResetPassword";
import ForgotPassword from "./pages/ForgotPassword";
import TopSongs from "./pages/TopSongs";
import AdminTop5Editor from "./pages/AdminTop5Editor";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import SubscriptionCancel from "./pages/SubscriptionCancel";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Roster from "./pages/Roster";
import Team from "./pages/Team";
import AdminRoster from "./pages/AdminRoster";
import AdminTeam from "./pages/AdminTeam";
import EditEvent from "./pages/EditEvent";
import CommunityIndex from "./pages/CommunityIndex";
import CommunityArticle from "./pages/CommunityArticle";
import AdminArticles from "./pages/AdminArticles";
import AdminHighlights from "./pages/AdminHighlights";
import AdminArticleEdit from "./pages/AdminArticleEdit";
import DoorAdmit from "./pages/DoorAdmit";
import RosterDetail from "./pages/RosterDetail";
import AdminCompTickets from "./pages/AdminCompTickets";
import AdminExternalEvents from "./pages/AdminExternalEvents";
import AdminAwinLinks from "./pages/AdminAwinLinks";
import AdminConcierge from "./pages/AdminConcierge";
import AdminSubmissions from "./pages/AdminSubmissions";
import AdminExternalSales from "./pages/AdminExternalSales";
import AdminDestinations from "./pages/AdminDestinations";
import Brands from "./pages/Brands";
import ConciergePage from "./pages/ConciergePage";
import SubmitMix from "./pages/SubmitMix";
import GrooveDanceBeatsSignup from "./pages/GrooveDanceBeatsSignup";

export default function App() {
  useEffect(() => {
    // Prime CSRF cookie so first POST won't 403
    ensureCsrf();

    const token = localStorage.getItem("accessToken");
    if (token && !shouldSkipRefresh()) {
      scheduleRefresh(token);
    }
  }, []);

  return (
    <HelmetProvider>
      <Router>
        <div className="min-h-screen flex flex-col">
          <EditorialHeader />
          <main id="main-content" className="flex-1 hlt-main" tabIndex={-1}>
            <Routes>
              {/* PUBLIC */}
              <Route path="/" element={<PremiumHome />} />
              <Route path="/admin/highlights" element={<ProtectedRoute allowedRoles={["ADMIN", "MASTER"]}><AdminHighlights /></ProtectedRoute>} />
              <Route path="/experiences" element={<ExperienceDiscovery />} />
              <Route path="/destinations" element={<ExperienceDiscovery destinations />} />
              <Route path="/destinations/:slug" element={<ExperienceDiscovery />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/verify" element={<VerifyAccount />} />
              <Route path="/payment-success" element={<PaymentSuccess />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/subscription/success" element={<SubscriptionSuccess />} />
              <Route path="/subscription/cancel" element={<SubscriptionCancel />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/community" element={<CommunityIndex />} />
              <Route path="/community/kaluki-528-ibiza-31-may-2026" element={<Kaluki528IbizaArticle />} />
              <Route path="/community/do-not-sleep-amnesia-ibiza-4-june-2026" element={<DoNotSleepAmnesiaJuneArticle />} />
              <Route path="/community/do-not-sleep-amnesia-ibiza-28-may-2026" element={<DoNotSleepAmnesiaArticle />} />
              <Route path="/community/carl-cox-unvrs-ibiza-12-july-2026" element={<CarlCoxUnvrsArticle />} />
              <Route path="/community/jamie-jones-paradise-unvrs-ibiza-22-july-2026" element={<JamieJonesParadiseUnvrsJulyArticle />} />
              <Route path="/community/jamie-jones-paradise-unvrs-ibiza-24-june-2026" element={<JamieJonesParadiseUnvrsArticle />} />
              <Route path="/community/joy-ibiza-528-5-july-2026" element={<JoyIbiza528Article />} />
              <Route path="/community/eastenderz-hi-ibiza-15-september-2026" element={<EastenderzHiIbizaFifteenSeptemberArticle />} />
              <Route path="/community/eastenderz-hi-ibiza-1-september-2026" element={<EastenderzHiIbizaSeptemberArticle />} />
              <Route path="/community/eastenderz-hi-ibiza-7-july-2026" element={<EastenderzHiIbizaSevenJulyArticle />} />
              <Route path="/community/eastenderz-hi-ibiza-16-june-2026" element={<EastenderzHiIbizaSixteenJuneArticle />} />
              <Route path="/community/eastenderz-hi-ibiza-9-june-2026" element={<EastenderzHiIbizaJuneArticle />} />
              <Route path="/community/eastenderz-hi-ibiza-2026-east-end-dubs" element={<EastenderzHiIbizaArticle />} />
              <Route path="/community/chris-stussy-uss-pacha-ibiza-2026" element={<ChrisStussyPachaArticle />} />
              <Route path="/community/:slug" element={<CommunityArticle />} />
              <Route path="/team" element={<Team />} />
              <Route path="/roster" element={<Roster />} />
              <Route path="/brands" element={<Brands />} />
              <Route path="/concierge" element={<ConciergePage />} />
              <Route path="/submit" element={<SubmitMix />} />
              <Route path="/top5-songs" element={<TopSongs />} />
              <Route path="/talent/:category/:slug" element={<RosterDetail />} />
              <Route path="/events/josh-baker-youme-amnesia-ibiza-2-july-2026" element={<JoshBakerYouMeArticle />} />
              <Route path="/events" element={<Events />} />
              <Route path="/groove-dance-beats" element={<GrooveDanceBeatsSignup />} />
              <Route path="/gdb" element={<GrooveDanceBeatsSignup />} />
              <Route path="/gdb-signup" element={<GrooveDanceBeatsSignup />} />

              {/* AUTH-ONLY */}
              <Route path="/purchase/:eventId" element={<ProtectedRoute><PurchaseTickets /></ProtectedRoute>} />
              <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
              <Route path="/subscribe" element={<ProtectedRoute><Subscription /></ProtectedRoute>} />
              <Route path="/song-submission" element={<ProtectedRoute><SongSubmission /></ProtectedRoute>} />

              {/* ROLE-GATED */}
              <Route path="/admin/events/create" element={<ProtectedRoute allowedRoles={["ADMIN", "MASTER"]}><CreateEvent /></ProtectedRoute>} />
              <Route path="/admin/events/:id/edit" element={<ProtectedRoute allowedRoles={["ADMIN", "MASTER"]}><EditEvent /></ProtectedRoute>} />
              <Route path="/admin/adminTop5Editor" element={<ProtectedRoute allowedRoles={["ADMIN", "MASTER"]}><AdminTop5Editor /></ProtectedRoute>} />
              <Route path="/admin/events" element={<ProtectedRoute allowedRoles={["ADMIN", "MASTER"]}><AdminEvents /></ProtectedRoute>} />
              <Route path="/admin/verification" element={<ProtectedRoute allowedRoles={["ADMIN", "MASTER"]}><AdminVerificationDashboard /></ProtectedRoute>} />
              <Route path="/admin/benefits" element={<ProtectedRoute allowedRoles={["ADMIN", "MASTER"]}><AdminBenefits /></ProtectedRoute>} />
              <Route path="/admin/roster" element={<ProtectedRoute allowedRoles={["ADMIN", "MASTER"]}><AdminRoster /></ProtectedRoute>} />
              <Route path="/admin/team" element={<ProtectedRoute allowedRoles={["ADMIN", "MASTER"]}><AdminTeam /></ProtectedRoute>} />
              <Route path="/affiliate/stats" element={<ProtectedRoute allowedRoles={["ADMIN", "MASTER", "AFFILIATE"]}><AffiliateStats /></ProtectedRoute>} />
              <Route path="/master/stats" element={<ProtectedRoute allowedRoles={["MASTER"]}><AllAffiliateStats /></ProtectedRoute>} />
              <Route path="/master/user" element={<ProtectedRoute allowedRoles={["MASTER"]}><MasterUser /></ProtectedRoute>} />
              <Route path="/ticket/scan" element={<ProtectedRoute allowedRoles={["ADMIN", "MASTER", "EVENT_WORKER"]}><QRScannerPage /></ProtectedRoute>} />
              <Route path="/admin/articles" element={<ProtectedRoute allowedRoles={["ADMIN", "MASTER"]}><AdminArticles /></ProtectedRoute>} />
              <Route path="/admin/articles/:id" element={<ProtectedRoute allowedRoles={["ADMIN", "MASTER"]}><AdminArticleEdit /></ProtectedRoute>} />
              <Route path="/admin/external-events" element={<ProtectedRoute allowedRoles={["ADMIN", "MASTER"]}><AdminExternalEvents /></ProtectedRoute>} />
              <Route path="/admin/awin-links" element={<ProtectedRoute allowedRoles={["ADMIN", "MASTER"]}><AdminAwinLinks /></ProtectedRoute>} />
              <Route path="/admin/concierge" element={<ProtectedRoute allowedRoles={["ADMIN", "MASTER"]}><AdminConcierge /></ProtectedRoute>} />
              <Route path="/admin/submissions" element={<ProtectedRoute allowedRoles={["ADMIN", "MASTER"]}><AdminSubmissions /></ProtectedRoute>} />
              <Route path="/admin/destinations" element={<ProtectedRoute allowedRoles={["ADMIN", "MASTER"]}><AdminDestinations /></ProtectedRoute>} />
              <Route path="/admin/external-sales" element={<ProtectedRoute allowedRoles={["MASTER"]}><AdminExternalSales /></ProtectedRoute>} />
              <Route path="/door-purchase" element={<ProtectedRoute allowedRoles={["ADMIN", "MASTER", "EVENT_WORKER"]}><DoorAdmit /></ProtectedRoute>} />
              <Route path="/master-tickets" element={<ProtectedRoute allowedRoles={["MASTER"]}><AdminCompTickets /></ProtectedRoute>} />
            </Routes>
          </main>
          <EditorialFooter />
        </div>
      </Router>
    </HelmetProvider>
  );
}

